import { useEffect } from "react";
import { log } from "@/utils/debug";

type ClinicalCode = {
  code: string;
  description: string;
  type: "ICD-10" | "OPCS-4";
  order?: number; // Add order property
};

interface FrontendClinicalCodesProps {
  codes?: ClinicalCode[];
  opcsLabel?: string;
}

export const FrontendClinicalCodes = ({ codes = [], opcsLabel }: FrontendClinicalCodesProps) => {
  // Log the codes received for debugging
  useEffect(() => {
    log.process("FrontendClinicalCodes received:", {
      totalCodes: codes.length,
      icd10Codes: codes.filter((c) => c.type === "ICD-10").length,
      opcs4Codes: codes.filter((c) => c.type === "OPCS-4").length,
      codes: codes.map(
        (code) =>
          `${code.type}: ${code.code} - ${code.description}${code.order !== undefined ? ` (order: ${code.order})` : ""}`,
      ),
    });
  }, [codes]);

  // Separate the codes by type, but preserve their order if it exists
  const icd10Codes = codes
    .filter((code) => code.type === "ICD-10")
    .sort((a, b) => {
      // Use order property if available, otherwise fall back to code value
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return a.code.localeCompare(b.code);
    });

  const opcsCodes = codes
    .filter((code) => code.type === "OPCS-4")
    .sort((a, b) => {
      // Use order property if available, otherwise fall back to code value
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return a.code.localeCompare(b.code);
    });

  return (
    <div className="space-y-4" data-testid="frontend-clinical-codes">
      <h2 className="text-base font-medium text-gray-600">Clinical Codes</h2>
      <div className="space-y-4 bg-[#F1F1F1] p-4 rounded-md">
        {icd10Codes.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">ICD-10</h3>
            {icd10Codes.map((code, index) => (
              <div key={index} className="text-sm">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{code.code}</span>
                  <span className="text-gray-600">|</span>
                  <span className="text-gray-600">{code.description}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {opcsCodes.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">{opcsLabel ?? "OPCS-4"}</h3>
            {opcsCodes.map((code, index) => (
              <div key={index} className="text-sm">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{code.code}</span>
                  <span className="text-gray-600">|</span>
                  <span className="text-gray-600">{code.description}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {codes.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-4">No clinical codes generated yet</div>
        )}
      </div>
    </div>
  );
};
